import React from 'react';

/* ── Spinner ─────────────────────────────────────────── */
export function Spinner({ size = 24 }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
      <div style={{
        width: size, height: size,
        border: '3px solid #e2e6f0',
        borderTopColor: '#4f6ef7',
        borderRadius: '50%',
        animation: 'spin 0.7s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/* ── Status Badge ────────────────────────────────────── */
const badgeStyles = {
  pending:   { background: '#fef3c7', color: '#d97706' },
  completed: { background: '#eef1ff', color: '#4f6ef7' },
  paid:      { background: '#dcfce7', color: '#16a34a' },
  approved:  { background: '#dcfce7', color: '#16a34a' },
  rejected:  { background: '#fee2e2', color: '#dc2626' },
  active:    { background: '#dcfce7', color: '#16a34a' },
  suspended: { background: '#fee2e2', color: '#dc2626' },
  admin:     { background: '#f0eeff', color: '#7c3aed' },
  user:      { background: '#eef1ff', color: '#4f6ef7' },
};

export function Badge({ status }) {
  const s = status?.toLowerCase() || 'pending';
  const style = badgeStyles[s] || { background: '#f0f2f8', color: '#636b89' };
  return (
    <span style={{
      ...style,
      padding: '3px 10px', borderRadius: 20,
      fontSize: 12, fontWeight: 700,
      display: 'inline-block', textTransform: 'capitalize',
    }}>
      {s}
    </span>
  );
}

/* ── Stat Card ───────────────────────────────────────── */
export function StatCard({ label, value, sub, color = '#4f6ef7', icon }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 18, padding: '22px',
      border: '1px solid #e2e6f0',
      boxShadow: '0 1px 4px rgba(15,18,51,.06)',
      transition: 'box-shadow .2s, transform .2s',
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(15,18,51,.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(15,18,51,.06)'; e.currentTarget.style.transform = 'none'; }}
    >
      {icon && (
        <div style={{
          width: 42, height: 42, borderRadius: 12,
          background: color + '18',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 14, fontSize: 18,
        }}>{icon}</div>
      )}
      <div style={{ fontSize: 12, fontWeight: 700, color: '#9099b4', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color, fontFamily: "'Sora', sans-serif" }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: '#16a34a', marginTop: 5 }}>{sub}</div>}
    </div>
  );
}

/* ── Card ────────────────────────────────────────────── */
export function Card({ children, style = {}, title, action }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 18, padding: '24px',
      border: '1px solid #e2e6f0',
      boxShadow: '0 1px 4px rgba(15,18,51,.06)',
      ...style,
    }}>
      {title && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1a1f5e', margin: 0 }}>{title}</h3>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

/* ── Alert ───────────────────────────────────────────── */
export function Alert({ type = 'error', message }) {
  if (!message) return null;
  const styles = {
    error:   { background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5' },
    success: { background: '#dcfce7', color: '#16a34a', border: '1px solid #86efac' },
    info:    { background: '#eef1ff', color: '#4f6ef7', border: '1px solid #c7d2fe' },
  };
  return (
    <div style={{
      ...styles[type],
      padding: '12px 16px', borderRadius: 10,
      fontSize: 14, marginBottom: 16,
    }}>
      {message}
    </div>
  );
}

/* ── Modal ───────────────────────────────────────────── */
export function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div onClick={onClose} className="ef-modal-overlay" style={{
      position: 'fixed', inset: 0,
      background: 'rgba(15,18,51,.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, backdropFilter: 'blur(4px)',
      padding: '20px',
    }}>
      <div onClick={e => e.stopPropagation()} className="ef-modal" style={{
        background: '#fff', borderRadius: 20,
        padding: 28, width: '100%', maxWidth: 460,
        boxShadow: '0 10px 40px rgba(15,18,51,.15)',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a1f5e', margin: 0, paddingRight: 12 }}>{title}</h3>
          <button onClick={onClose} style={{
            background: '#f0f2f8', border: 'none', borderRadius: '50%',
            width: 32, height: 32, cursor: 'pointer', fontSize: 18,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ── Form Input ──────────────────────────────────────── */
export function FormInput({ label, error, ...props }) {
  return (
    <div style={{ marginBottom: 16 }}>
      {label && <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#2e3456', marginBottom: 6 }}>{label}</label>}
      <input {...props} style={{
        width: '100%', padding: '11px 14px',
        border: `1.5px solid ${error ? '#dc2626' : '#e2e6f0'}`,
        borderRadius: 10, fontSize: 14, color: '#0f1233',
        outline: 'none', background: '#fff',
        boxSizing: 'border-box',
        transition: 'border-color .2s',
        ...props.style,
      }}
        onFocus={e => { e.target.style.borderColor = '#4f6ef7'; e.target.style.boxShadow = '0 0 0 3px rgba(79,110,247,.1)'; }}
        onBlur={e => { e.target.style.borderColor = error ? '#dc2626' : '#e2e6f0'; e.target.style.boxShadow = 'none'; }}
      />
      {error && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{error}</p>}
    </div>
  );
}

/* ── Select ──────────────────────────────────────────── */
export function FormSelect({ label, options, ...props }) {
  return (
    <div style={{ marginBottom: 16 }}>
      {label && <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#2e3456', marginBottom: 6 }}>{label}</label>}
      <select {...props} style={{
        width: '100%', padding: '11px 14px',
        border: '1.5px solid #e2e6f0',
        borderRadius: 10, fontSize: 14, color: '#0f1233',
        outline: 'none', background: '#fff',
        boxSizing: 'border-box', cursor: 'pointer',
        ...props.style,
      }}>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

/* ── Button ──────────────────────────────────────────── */
export function Btn({ children, variant = 'primary', loading: isLoading, style = {}, ...props }) {
  const variants = {
    primary:  { background: '#4f6ef7', color: '#fff', border: 'none' },
    outline:  { background: 'transparent', color: '#1a1f5e', border: '1.5px solid #c8cedf' },
    danger:   { background: '#dc2626', color: '#fff', border: 'none' },
    success:  { background: '#16a34a', color: '#fff', border: 'none' },
    ghost:    { background: '#f0f2f8', color: '#636b89', border: 'none' },
  };
  return (
    <button
      disabled={isLoading || props.disabled}
      {...props}
      style={{
        ...variants[variant],
        padding: '10px 22px', borderRadius: 10,
        fontSize: 14, fontWeight: 600,
        cursor: isLoading ? 'not-allowed' : 'pointer',
        opacity: (isLoading || props.disabled) ? 0.85 : 1,
        transition: 'all .2s',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        ...style,
      }}
    >
      {isLoading ? (
        <span
          aria-label="Loading"
          style={{
            width: 18, height: 18,
            border: '2.5px solid rgba(255,255,255,.35)',
            borderTopColor: 'currentColor',
            borderRadius: '50%',
            display: 'inline-block',
            animation: 'efBtnSpin .7s linear infinite',
          }}
        />
      ) : children}
      <style>{`@keyframes efBtnSpin { to { transform: rotate(360deg); } }`}</style>
    </button>
  );
}

/* ── Empty State ─────────────────────────────────────── */
export function EmptyState({ icon = '📭', message = 'No data yet.' }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px', color: '#9099b4' }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
      <p style={{ fontSize: 14 }}>{message}</p>
    </div>
  );
}

/* ── Avatar ──────────────────────────────────────────── */
const COLORS = ['#4f6ef7','#16a34a','#f4a61d','#dc2626','#7c3aed','#0891b2'];
export function Avatar({ name = '', size = 36 }) {
  const initials = name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  const color    = COLORS[name.charCodeAt(0) % COLORS.length];
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: color, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.38, fontWeight: 700, flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

export function fmt(n) {
  return `$${parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function fmtDate(d) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
